import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { __ } from '@wordpress/i18n';
import { useForm } from 'react-hook-form';
import { is, isNil } from 'ramda';
import { klona } from 'klona';

// tools
import formatDateString from '../../helpers/formatDateString';

// store
import { storeSet } from '../../store';

// components
import FormField from '../FormField';
import ButtonFlat from '../ButtonWrapper/components/ButtonFlat';
import ButtonFormSubmit from '../ButtonWrapper/components/ButtonFormSubmit';
import ModalDialog from '../ModalDialog';
import PlusIcon from '../../icons/PlusIcon';
import ButtonLabeled from '../ButtonWrapper/components/ButtonLabeled';
import handleSuccessAndDisplay from '../../helpers/handleSuccessAndDisplay';

const ModalCreateProject = ({ type = 'dashboard', editProject = {} }) => {
    const navigate = useNavigate();
    const [modalVisible, setModalVisible] = useState(false);
    const [loading, setLoading] = useState(false);
    const getFormattedData = (data) => {
        data = {
            'title': data.title,
            'company': data.company,
            'client': data.client,
            'director': data.director,
            'deadline': data.deadline,
            'budget': data.budget
        }
        return data
    }
    const {
        control,
        handleSubmit,
        formState: { isValid },
        reset
    } = useForm({ defaultValues: getFormattedData(klona(editProject)), mode: 'onChange' });

    const onSubmit = async (formData) => {
        const updatedFormData = klona({ ...formData });
        delete updatedFormData.image;
        const formDataToSend = new FormData();

        if (!isNil(editProject.id)) {
            updatedFormData.id = editProject.id;
        }

        updatedFormData.deadline = is(String, updatedFormData.deadline) ? updatedFormData.deadline : formatDateString(updatedFormData.deadline);
        formDataToSend.append('Payload', JSON.stringify(updatedFormData));

        if (formData.image) {
            formDataToSend.append('thumbnail', formData.image);
        }

        setLoading(true);

        try {
            await storeSet('createUpdateProject', formDataToSend);

            if (!isNil(editProject.id)) {
                // After updating, navigate to the project page
                navigate(`/projects/${editProject.id}`);
            } else {
                handleSuccessAndDisplay(__('Progetto creato con successo!', 'soundbetween'))
            }

            setModalVisible(false);
        } catch (error) {
            // Error already displayed by store action
        } finally {
            setLoading(false);
        }
    };

    const handleCreateNewProject = () => {
        setModalVisible(true);
    }

    const handleCloseModal = () => {
        reset();
        setModalVisible(false);
    }

    return (
        <>
            {!isNil(editProject.id)
                ? <ButtonFlat onClick={handleCreateNewProject}>
                    {__('Modifica', 'soundbetween')}
                </ButtonFlat>
                : 'dashboard' === type
                    ? <ButtonLabeled onClick={handleCreateNewProject}>
                        <PlusIcon/>
                        <span>{__('Crea progetto!', 'soundbetween')}</span>
                    </ButtonLabeled>
                    : <ButtonFlat
                        onClick={handleCreateNewProject}>
                        {__('+ Nuovo progetto', 'soundbetween')}
                    </ButtonFlat>}
            {modalVisible
                ? <ModalDialog
                    additionalClasses={['modalCreateProject']}
                    header={!isNil(editProject.id)
                        ? __('Modifica il progetto', 'soundbetween')
                        : __('Crea un nuovo progetto', 'soundbetween')}
                    visible={modalVisible}
                    onSubmit={handleSubmit(onSubmit)}
                    onHide={handleCloseModal}>
                    <form className="appForm formInModal" onSubmit={handleSubmit(onSubmit)}>
                        <FormField
                            type="text"
                            name="title"
                            label={__('Titolo', 'soundbetween')}
                            placeholder={__('Digita nome progetto', 'soundbetween')}
                            control={control}
                            config={{ required: __('È obbligatorio', 'soundbetween') }}
                        />

                        <FormField
                            type="text"
                            name="client"
                            label={__('Casa di produzione', 'soundbetween')}
                            placeholder={__('Digita nome', 'soundbetween')}
                            control={control}
                            rules={{ required: __('È obbligatorio', 'soundbetween') }}
                        />

                        <FormField
                            type="text"
                            name="company"
                            label={__('Cliente', 'soundbetween')}
                            placeholder={__('Nome e Cognome', 'soundbetween')}
                            control={control}
                            rules={{ required: __('È obbligatorio', 'soundbetween') }}
                        />

                        <FormField
                            type="text"
                            name="director"
                            label={__('Director', 'soundbetween')}
                            placeholder={__('Nome e Cognome', 'soundbetween')}
                            control={control}
                            rules={{ required: __('È obbligatorio', 'soundbetween') }}
                        />

                        <FormField
                            type="datepicker"
                            name="deadline"
                            label={__('Deadline', 'soundbetween')}
                            placeholder={__('', 'soundbetween')}
                            control={control}
                            rules={{
                                required: __('È obbligatorio', 'soundbetween'),
                                validate: (value) => {
                                    if (!value) return true;
                                    const today = new Date();
                                    today.setHours(0, 0, 0, 0);
                                    return value > today || __('La data deve essere nel futuro', 'soundbetween');
                                }
                            }}
                            shouldDisableDate={(date) => {
                                const today = new Date();
                                today.setHours(0, 0, 0, 0);
                                return date <= today;
                            }}
                        />

                        <FormField
                            type="numeric"
                            name="budget"
                            label={__('Budget', 'soundbetween')}
                            placeholder="0.00"
                            prefix="€"
                            control={control}
                            rules={{
                                required: 'Price is required',
                                pattern: {
                                    value: /^\d+(\.\d{1,2})?$/,
                                    message: 'Please enter a valid price with up to 2 decimal places'
                                },
                                validate: (value) => {
                                    const numValue = parseFloat(value);
                                    if (isNaN(numValue) || numValue <= 0) {
                                        return 'Price must be greater than 0';
                                    }
                                    return true;
                                }
                            }}
                        />

                        <FormField
                            type="fileupload"
                            name="image"
                            label={__('Immagine di copertina', 'soundbetween')}
                            placeholder={__('Scegli un file', 'soundbetween')}
                            control={control}
                            accept="image/*"
                            rules={isNil(editProject.id)
                                ? { required: __('È obbligatorio', 'soundbetween') }
                                : {}}
                        />

                        <div className="appFormActions">
                            <ButtonFlat size="lg" type="button" onClick={handleCloseModal}>
                                {__('Anulla', 'soundbetween')}
                            </ButtonFlat>
                            <ButtonFormSubmit disabled={loading || !isValid}>
                                {__('Salva e continua', 'soundbetween')}
                            </ButtonFormSubmit>
                        </div>
                    </form>
                </ModalDialog> : null}
        </>
    )
}

export default ModalCreateProject;